/**
 * UI 向けエラー・案内文の集約モジュール。
 *
 * 設計意図 (issue #38, #43):
 *   - 「不明なエラー」「読み込みエラー」のように **何が起きたか／何をすればいいか** が
 *     伝わらないフォールバック文言を一掃する
 *   - 文末は常に **「次に何をすべきか」を含むアクション付き** にする
 *     例: 「○○できませんでした。△△を確認のうえ、もう一度お試しください。」
 *
 * 設計ルール:
 *   - リテラシー低めの動画販売業者を読者として書く
 *   - 「不明なエラー」「読み込みエラー」「読み込みに失敗しました」は禁止語（テストで検出）
 *   - サーバから具体的なメッセージ (ApiError.message) が返っている場合はそれを優先し、
 *     無い時のフォールバックとしてこの辞書を使う
 *   - API/メールと共有する文章は @terafile/shared の USER_MESSAGES を参照
 */

import { USER_MESSAGES } from '@terafile/shared'

export { USER_MESSAGES }

/**
 * データ取得（GET 系）系の失敗フォールバック。
 * 「読み込みエラー」「読み込みに失敗しました」の置き換え。
 */
export const FETCH_ERROR_FALLBACK =
  'データを読み込めませんでした。少し時間を置いてからページを再読み込みしてください。'

/**
 * ログイン失敗のフォールバック。
 */
export const LOGIN_ERROR_FALLBACK =
  'ログインできませんでした。メールアドレスとパスワードをお確かめのうえ、もう一度お試しください。ネットワーク接続もご確認ください。'

/**
 * アカウント作成失敗のフォールバック。
 */
export const SIGNUP_ERROR_FALLBACK =
  'アカウントを作成できませんでした。入力内容をお確かめのうえ、もう一度お試しください。ネットワーク接続もご確認ください。'

/**
 * OAuth コールバック失敗のフォールバック。
 */
export const AUTH_CALLBACK_ERROR_FALLBACK =
  'ログインを完了できませんでした。最初からやり直してください。'

/**
 * 一般的な「保存/送信に失敗した」フォールバック（書き込み系）。
 *
 * 動詞ごとに細かく文言を分けず、これ 1 本に集約する方針。
 * 「何を試みたか」は呼び出し側 UI（ボタン直後の toast 等）の文脈で伝わるため、
 * メッセージは「次のアクション」だけに集中させる。
 */
export const SUBMIT_ERROR_FALLBACK =
  '操作に失敗しました。少し時間を置いてから、もう一度お試しください。'

/**
 * 共有URL（着地ページ）で失効/期限切れに使う共通文言。
 *
 * 失効と期限切れはユーザーから見て区別する意味が無いので同一文言にする。
 */
export const SHARE_LINK_UNAVAILABLE = USER_MESSAGES.shareLinkUnavailable

/**
 * ApiError ライク（message プロパティを持つ）の型ガード。
 */
function hasMessage(err: unknown): err is { message: string } {
  return (
    typeof err === 'object' &&
    err !== null &&
    'message' in err &&
    typeof (err as { message: unknown }).message === 'string'
  )
}

/**
 * Supabase Auth の英語エラーメッセージを日本語に変換するマップ。
 *
 * 自社 API のエラー（既に日本語）はそのまま素通しさせ、Supabase 由来の英語だけを
 * UI 向けの日本語に置換する。完全一致と正規表現マッチの 2 段階で判定する。
 *
 * 参考: https://supabase.com/docs/reference/javascript/auth-api (エラーメッセージ一覧)
 */
const SUPABASE_AUTH_MESSAGES_EXACT: Record<string, string> = {
  'Invalid login credentials':
    'メールアドレスまたはパスワードが違います。お確かめのうえ、もう一度お試しください。',
  'Email not confirmed':
    'メールアドレスの確認が完了していません。届いた確認メールのリンクをクリックしてください。',
  'User already registered':
    'このメールアドレスは既に登録されています。ログイン画面からお進みください。',
  'A user with this email address has already been registered':
    'このメールアドレスは既に登録されています。ログイン画面からお進みください。',
  'Email address is invalid':
    'メールアドレスの形式が正しくありません。お確かめのうえ、もう一度お試しください。',
  'Signup requires a valid password': 'パスワードを入力してください。',
  'Invalid email or password':
    'メールアドレスまたはパスワードが違います。お確かめのうえ、もう一度お試しください。',
  'User not found': '該当するアカウントが見つかりませんでした。メールアドレスをお確かめください。',
  'New password should be different from the old password':
    '新しいパスワードは現在のパスワードと違うものを設定してください。',
  'Auth session missing!': 'ログインセッションが切れています。再度ログインしてください。',
  'Token has expired or is invalid': 'リンクの有効期限が切れています。最初からやり直してください。',
  'Email link is invalid or has expired':
    '認証リンクが無効か期限切れです。もう一度メール送信からやり直してください。',
}

/**
 * 可変部分を含むメッセージ用の正規表現マッチング。
 */
const SUPABASE_AUTH_MESSAGES_REGEX: Array<{
  pattern: RegExp
  message: string
}> = [
  {
    pattern: /^Password should be at least \d+ characters/i,
    message: 'パスワードはもう少し長く設定してください。',
  },
  {
    pattern: /^Email rate limit/i,
    message: 'メール送信の上限に達しました。少し時間を置いてから、もう一度お試しください。',
  },
  {
    pattern: /^For security purposes, you can only request this/i,
    message: 'セキュリティのため、しばらく時間を置いてから、もう一度お試しください。',
  },
  {
    pattern: /^Unable to validate email address/i,
    message: 'メールアドレスの形式が正しくありません。お確かめのうえ、もう一度お試しください。',
  },
  {
    pattern: /^User from sub claim in JWT does not exist/i,
    message: 'アカウント情報が見つかりませんでした。再度ログインしてください。',
  },
  {
    pattern: /^Failed to fetch$|NetworkError|Network request failed/i,
    message:
      'サーバーに接続できませんでした。ネットワーク接続をご確認のうえ、もう一度お試しください。',
  },
]

/**
 * 既知の英語メッセージを日本語に変換する。該当が無ければ null を返す。
 */
function translateSupabaseAuthMessage(message: string): string | null {
  if (SUPABASE_AUTH_MESSAGES_EXACT[message]) {
    return SUPABASE_AUTH_MESSAGES_EXACT[message]
  }
  for (const entry of SUPABASE_AUTH_MESSAGES_REGEX) {
    if (entry.pattern.test(message)) {
      return entry.message
    }
  }
  return null
}

/**
 * 任意のエラーオブジェクトを「ユーザー向けエラーメッセージ」に変換する。
 *
 * 優先順位:
 *   1. Supabase Auth の既知の英語メッセージ → 日本語に変換
 *   2. err.message が日本語等そのまま使える文字列 → そのまま返す（自社 API レスポンス想定）
 *   3. それ以外 → fallback を返す
 *
 * @example
 *   try { ... } catch (err) {
 *     setError(toUserMessage(err, FETCH_ERROR_FALLBACK))
 *   }
 */
export function toUserMessage(err: unknown, fallback: string): string {
  if (!hasMessage(err)) return fallback
  const message = err.message.trim()
  if (message.length === 0) return fallback

  // Supabase Auth の英語メッセージなら日本語にマップ
  const translated = translateSupabaseAuthMessage(message)
  if (translated) return translated

  // それ以外（自社 API 等の日本語メッセージ）はそのまま返す
  return message
}

/**
 * Google アカウントの紐付け・切り替えが失敗して戻ってきたときの文言。
 * 引数は Supabase が戻り先の URL に付ける error_code（または error）。
 */
const GOOGLE_LINK_ERROR_MESSAGES: Record<string, string> = {
  // 選んだ Google アカウントが、別のテラファイル便アカウント（または自分の別の紐付け）で使われている
  identity_already_exists:
    'その Google アカウントは、すでにテラファイル便で使われています。これまでの Google アカウントの紐付けはそのままです。別の Google アカウントを選んでください。',
  access_denied:
    'Google アカウントの選択が取りやめられました。これまでの紐付けはそのままです。切り替える場合は、もう一度お試しください。',
  manual_linking_disabled:
    'いまは Google アカウントの紐付けを受け付けていません。お手数ですが運営にお問い合わせください。',
}

export function googleLinkErrorMessage(errorCode: string): string {
  return (
    GOOGLE_LINK_ERROR_MESSAGES[errorCode] ??
    'Google アカウントを紐付けられませんでした。これまでの紐付けはそのままです。少し時間を置いてから、もう一度お試しください。'
  )
}
